import { useEffect, useRef, useState } from "react";

/**
 * Cronômetro de auto-atualização dos dashboards.
 *
 * Encapsula todo o estado e as funções de controle do timer:
 *  - `counter` / `setCounter`: segundos decorridos (0–60) exibidos no anel.
 *  - `loadingCronometro` / `setLoadingCronometro`: trava visual durante o fetch.
 *  - `startCronometro` / `stopCronometro` / `resetCronometro`: controle do intervalo.
 *
 * A cada 60s o contador dispara `onComplete` (re-fetch dos dados) e volta a zero.
 * O auto-refresh só roda enquanto houver um `selectTime` definido — quando o
 * usuário aplica um intervalo/zoom manual (`selectTime` nulo) a contagem para.
 *
 * @param {Object}   [params]
 * @param {number}   [params.selectTime] índice de tempo selecionado no layout.
 * @param {Function} [params.onComplete] callback disparado quando `counter === 60`.
 */
export default function useCronometro({ selectTime, onComplete } = {}) {
  const [counter, setCounter] = useState(null);
  const [loadingCronometro, setLoadingCronometro] = useState(false);
  const cronometroRef = useRef(null);

  // Mantém sempre a versão mais recente do callback sem recriar o efeito a cada tick.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const startCronometro = () => {
    if (selectTime || selectTime === 0) {
      if (cronometroRef.current) {
        clearInterval(cronometroRef.current);
      }
      setCounter(0);
      cronometroRef.current = setInterval(() => {
        setCounter((prev) => {
          if (prev >= 60) {
            return 0;
          } else {
            return prev + 1;
          }
        });
      }, 1000);
    } else {
      setCounter(0);
      clearInterval(cronometroRef.current);
    }
  };

  const stopCronometro = () => {
    clearInterval(cronometroRef.current);
    cronometroRef.current = null;
  };

  // Usada pelos filtros (cliente/serviço/host/interface): pausa a contagem e
  // limpa o display sem descartar o ref do intervalo.
  const resetCronometro = () => {
    clearInterval(cronometroRef.current);
    setCounter(null);
  };

  // Auto-atualização: ao completar 60s dispara o re-fetch.
  useEffect(() => {
    if (counter === 60) {
      onCompleteRef.current?.();
    }
  }, [counter]);

  // Mata o intervalo junto com o componente. Sem isto o timer continua rodando
  // depois de sair da dash (a `3.8-flow`, por exemplo, não chama stopCronometro
  // ao desmontar), mantendo toda a árvore do layout viva na memória.
  useEffect(() => {
    return () => {
      clearInterval(cronometroRef.current);
      cronometroRef.current = null;
    };
  }, []);

  return {
    counter,
    setCounter,
    loadingCronometro,
    setLoadingCronometro,
    startCronometro,
    stopCronometro,
    resetCronometro,
  };
}
